import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Plus, MagnifyingGlass, ArrowsClockwise, Plug, CaretRight, CaretUp, CaretDown } from '@phosphor-icons/react';
import { formatLabel, STATUS_STYLES, RISK_STYLES, timeAgo } from '@/lib/format';
import { COUNTRY_NAMES, filterCountries, validateName, validateDOB, validateIdNumber, ID_TYPES } from '@/lib/constants';
import { displayRelation, RELATION_OPTIONS } from '@/lib/relations';

// ─── Feature toggles ─────────────────────────────────────────────
// Set to true to re-show the "Quick Screen" button + modal on the Cases page.
// Underlying screening endpoints, modal JSX, and handler code remain fully
// intact — this only hides the entry point from the UI so users cannot trigger it.
const SHOW_QUICK_SCREEN = false;

export default function CasesPage() {
  const [tab, setTab] = useState('cases');
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const PAGE_SIZE = 25;
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showScreen, setShowScreen] = useState(false);
  const [screenName, setScreenName] = useState('');
  const [screenResults, setScreenResults] = useState(null);
  const [screening, setScreening] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { document.title = "Radar — Cases"; }, []);

  // Incoming state
  const [configs, setConfigs] = useState([]);
  const [pulls, setPulls] = useState([]);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [configForm, setConfigForm] = useState({ name: '', api_url: '', method: 'GET', auth_type: 'none', auth_value: '', response_path: '', field_mapping: '{}', default_case_type: 'individual', is_active: true });
  const [pulling, setPulling] = useState(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (riskFilter) params.risk_rating = riskFilter;
      if (typeFilter) params.case_type = typeFilter;
      const r = await api.get('/cases', { params });
      setCases(r.data.cases);
      setTotal(r.data.total);
    } catch (e) { }
    setLoading(false);
  }, [search, statusFilter, riskFilter, typeFilter, page]);

  const fetchIncoming = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get('/incoming/configs'), api.get('/incoming/history')]);
      setConfigs(c.data.configs);
      setPulls(p.data.pulls);
    } catch (e) { }
  }, []);

  useEffect(() => { if (tab === 'cases') fetchCases(); else fetchIncoming(); }, [tab, fetchCases, fetchIncoming]);

  const handleCreateCase = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const body = { case_type: fd.get('case_type'), full_name: fd.get('full_name'), nationality: fd.get('nationality'), country_of_residence: fd.get('country_of_residence'), date_of_birth: fd.get('date_of_birth') || null, id_type: fd.get('id_type') || null, id_number: fd.get('id_number') || null, source_of_wealth: fd.get('source_of_wealth') || null, source: 'Manual', notes: fd.get('notes') || null };
    try {
      await api.post('/cases', body);
      setShowCreate(false);
      fetchCases();
    } catch (err) { alert(err.response?.data?.detail || 'Error creating case'); }
  };

  const handleScreen = async () => {
    if (!screenName.trim()) return;
    setScreening(true);
    try {
      const r = await api.post('/screening/single', { name: screenName, threshold: 40.0, databases: ['sanctions', 'pep', 'watchlist'] });
      setScreenResults(r.data);
    } catch (e) { alert('Screening failed'); }
    setScreening(false);
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      let mapping = {};
      try { mapping = JSON.parse(configForm.field_mapping); } catch { mapping = {}; }
      await api.post('/incoming/configs', { ...configForm, field_mapping: mapping, custom_headers: null });
      setShowConfigForm(false);
      setConfigForm({ name: '', api_url: '', method: 'GET', auth_type: 'none', auth_value: '', response_path: '', field_mapping: '{}', default_case_type: 'individual', is_active: true });
      fetchIncoming();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handlePull = async (configId) => {
    setPulling(configId);
    try {
      const r = await api.post(`/incoming/pull/${configId}`);
      alert(`Success! ${r.data.cases_created} cases created from ${r.data.records_received} records.`);
      fetchIncoming();
      fetchCases();
    } catch (err) { alert(err.response?.data?.detail || 'Pull failed'); }
    setPulling(null);
  };

  const handleDeleteConfig = async (id) => {
    if (!window.confirm('Delete this config?')) return;
    await api.delete(`/incoming/configs/${id}`);
    fetchIncoming();
  };

  return (
    <div data-testid="cases-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">Cases & Screening</h1>
          <p className="text-sm text-[#52525B]">Manage cases, run screenings, and configure incoming data</p>
        </div>
        <div className="flex gap-2">
          {SHOW_QUICK_SCREEN && (
            <button onClick={() => setShowScreen(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] transition-all" data-testid="quick-screen-btn">
              <MagnifyingGlass size={16} /> Quick Screen
            </button>
          )}
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] transition-all new-case-button">
            <Plus size={16} /> New Case
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E4E4E7]">
        {[{ id: 'cases', label: 'All Cases', icon: '📋' }, { id: 'incoming', label: 'Incoming', icon: '🔌' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${tab === t.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* CASES TAB */}
      {tab === 'cases' && (
        <div>
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search by name..." className="w-full pl-9 pr-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#0052FF]" />
            </div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
              <option value="">All Statuses</option>
              {['draft', 'pending_screening', 'under_review', 'cleared', 'escalated', 'rejected', 'suspended'].map(s => <option key={s} value={s}>{formatLabel(s)}</option>)}
            </select>
            <select value={riskFilter} onChange={e => { setRiskFilter(e.target.value); setPage(0); }} className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
              <option value="">All Risk Levels</option>
              {['Low', 'Medium', 'High', 'Very High', 'Prohibited'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }} className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
              <option value="">All Types</option>
              <option value="individual">Individual</option>
              <option value="entity">Entity</option>
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E4E4E7]">
                    {[{col:'full_name',label:'Name'},{col:'',label:'Account'},{col:'case_type',label:'Type'},{col:'status',label:'Status'},{col:'risk_rating',label:'Risk'},{col:'',label:'Monitoring'},{col:'',label:'Source'},{col:'updated_at',label:'Updated'},{col:'',label:''}].map(h => (
                      <th key={h.label} onClick={() => h.col && (() => { if (sortCol === h.col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(h.col); setSortDir('asc'); } })()}
                        className={`text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B] ${h.col ? 'cursor-pointer hover:text-[#0052FF] select-none' : ''}`}>
                        {h.label} {h.col && sortCol === h.col && (sortDir === 'asc' ? <CaretUp size={10} className="inline" /> : <CaretDown size={10} className="inline" />)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...cases].sort((a, b) => {
                    if (!sortCol) return 0;
                    const STATUS_ORDER = ['draft','pending_screening','under_review','escalated','suspended','rejected','cleared'];
                    const RISK_ORDER = ['Low','Medium','High','Very High','Prohibited'];
                    let va = a[sortCol], vb = b[sortCol];
                    if (sortCol === 'status') { va = STATUS_ORDER.indexOf(va); vb = STATUS_ORDER.indexOf(vb); }
                    else if (sortCol === 'risk_rating') { va = RISK_ORDER.indexOf(va); vb = RISK_ORDER.indexOf(vb); }
                    else if (sortCol === 'updated_at') { va = new Date(va || 0).getTime(); vb = new Date(vb || 0).getTime(); }
                    else { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
                    if (va < vb) return sortDir === 'asc' ? -1 : 1;
                    if (va > vb) return sortDir === 'asc' ? 1 : -1;
                    return 0;
                  }).map(c => (
                    <tr key={c.id} data-testid={`case-row-${c.id}`} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8] cursor-pointer transition-all" onClick={() => navigate(`/cases/${c.id}`)}>
                      <td className="px-4 py-3 text-sm font-medium text-[#0A0A0A]">{c.full_name}{c.account_relation ? <span className="ml-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]">· {displayRelation(c.account_relation)}</span> : null}</td>
                      <td className="px-4 py-3 text-xs">{c.account_name ? <span className="text-[#0052FF]">{c.account_name}</span> : <span className="text-[#A1A1AA]">—</span>}{c.account_code ? <span className="block text-[10px] font-mono text-[#A1A1AA]">{c.account_code}</span> : null}</td>
                      <td className="px-4 py-3 text-xs font-medium text-[#52525B]">{formatLabel(c.case_type)}</td>
                      <td className="px-4 py-3"><span className={STATUS_STYLES[c.status] || 'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100'}>{formatLabel(c.status)}</span></td>
                      <td className="px-4 py-3"><span className={RISK_STYLES[c.risk_rating] || 'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100'}>{c.risk_rating}</span></td>
                      <td className="px-4 py-3"><span className={`inline-block w-2.5 h-2.5 rounded-full ${c.monitoring_enabled ? 'bg-green-500' : 'bg-gray-300'}`} /></td>
                      <td className="px-4 py-3 text-xs text-[#52525B]">{c.source || 'Manual'}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{timeAgo(c.updated_at)}</td>
                      <td className="px-4 py-3"><CaretRight size={14} className="text-[#A1A1AA]" /></td>
                    </tr>
                  ))}
                  {cases.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center"><p className="text-sm text-[#52525B] mb-2">No cases yet</p><p className="text-xs text-[#A1A1AA]">Create your first case to begin AML screening.</p></td></tr>}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-[#E4E4E7] flex items-center justify-between">
                <span className="text-xs text-[#A1A1AA]">Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} cases</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-40">Previous</button>
                  <span className="px-3 py-1 text-xs text-[#52525B]">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-40">Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INCOMING TAB */}
      {tab === 'incoming' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold">API Integrations</h2>
            <button onClick={() => setShowConfigForm(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">
              <Plug size={16} /> Add API Source
            </button>
          </div>

          {configs.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center">
              <Plug size={40} className="mx-auto text-[#A1A1AA] mb-3" />
              <p className="text-sm text-[#52525B] mb-2">No API sources configured</p>
              <p className="text-xs text-[#A1A1AA]">Connect to external systems like Tychi Investor Portal to automatically create cases</p>
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {configs.map(cfg => (
                <div key={cfg.id} className="bg-white border border-[#E4E4E7] rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-[#0A0A0A]">{cfg.name}</h3>
                      <p className="text-xs text-[#A1A1AA] mt-0.5 font-mono">{cfg.method} {cfg.api_url}</p>
                      <div className="flex gap-4 mt-2 text-xs text-[#52525B]">
                        <span>Auth: {cfg.auth_type}</span>
                        <span>Type: {cfg.default_case_type}</span>
                        {cfg.last_pulled_at && <span>Last pull: {new Date(cfg.last_pulled_at).toLocaleString()}</span>}
                        <span>Total created: {cfg.total_cases_created || 0}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handlePull(cfg.id)} disabled={pulling === cfg.id} className="px-3 py-1.5 text-xs font-medium bg-[#0052FF] text-white rounded hover:bg-[#0040CC] disabled:opacity-50">
                        {pulling === cfg.id ? 'Pulling...' : 'Pull Data'}
                      </button>
                      <button onClick={() => handleDeleteConfig(cfg.id)} className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pulls.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading text-base font-semibold mb-3">Pull History</h3>
              <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E4E4E7]">
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Source</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Date</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Records</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Cases Created</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
                  </tr></thead>
                  <tbody>
                    {pulls.map((p, i) => (
                      <tr key={i} className="border-b border-[#E4E4E7]">
                        <td className="px-4 py-2 text-sm">{p.config_name}</td>
                        <td className="px-4 py-2 text-xs text-[#52525B]">{new Date(p.pulled_at).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm">{p.records_received}</td>
                        <td className="px-4 py-2 text-sm font-medium">{p.cases_created}</td>
                        <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${p.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 6A: Case Creation Wizard (5-step) */}
      {showCreate && (
        <CaseCreationWizard onClose={() => setShowCreate(false)} onComplete={(caseId) => { setShowCreate(false); fetchCases(); if (caseId) navigate(`/cases/${caseId}`); }} />
      )}

      {/* Quick Screen Modal */}
      {showScreen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => { setShowScreen(false); setScreenResults(null); }}>
          <div className="bg-white rounded-md border border-[#E4E4E7] shadow-lg w-full max-w-xl p-6" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-semibold mb-4">Quick Screening</h2>
            <div className="flex gap-2 mb-4">
              <input value={screenName} onChange={e => setScreenName(e.target.value)} placeholder="Enter name to screen..." className="flex-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" onKeyDown={e => e.key === 'Enter' && handleScreen()} />
              <button onClick={handleScreen} disabled={screening} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">{screening ? 'Screening...' : 'Screen'}</button>
            </div>
            {screenResults && (
              <div className="max-h-80 overflow-auto">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${screenResults.status === 'match' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{screenResults.status === 'match' ? `${screenResults.total_matches} Match(es)` : 'Clear'}</span>
                </div>
                {screenResults.matches?.slice(0, 10).map((m, i) => (
                  <div key={i} className="border border-[#E4E4E7] rounded-md p-3 mb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium">{m.entry?.name || m.matched_name}</p>
                        <p className="text-xs text-[#52525B]">{m.database} {m.entry?.source ? `• ${m.entry.source}` : ''}</p>
                      </div>
                      <span className={`text-sm font-bold ${m.match_score >= 80 ? 'text-red-600' : m.match_score >= 60 ? 'text-orange-600' : 'text-yellow-600'}`}>{m.match_score?.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add API Config Modal */}
      {showConfigForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowConfigForm(false)}>
          <div className="bg-white rounded-md border border-[#E4E4E7] shadow-lg w-full max-w-lg p-6 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-semibold mb-4">Configure API Source</h2>
            <form onSubmit={handleSaveConfig} className="space-y-3">
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Source Name</label>
                <input value={configForm.name} onChange={e => setConfigForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. Tychi Investor Portal" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-1"><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Method</label>
                  <select value={configForm.method} onChange={e => setConfigForm(p => ({ ...p, method: e.target.value }))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md"><option>GET</option><option>POST</option></select></div>
                <div className="col-span-3"><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">API URL</label>
                  <input value={configForm.api_url} onChange={e => setConfigForm(p => ({ ...p, api_url: e.target.value }))} required placeholder="https://api.example.com/investors" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md font-mono text-xs" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Auth Type</label>
                  <select value={configForm.auth_type} onChange={e => setConfigForm(p => ({ ...p, auth_type: e.target.value }))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md"><option value="none">None</option><option value="bearer">Bearer Token</option><option value="api_key">API Key</option><option value="basic">Basic Auth</option></select></div>
                <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Auth Value</label>
                  <input value={configForm.auth_value} onChange={e => setConfigForm(p => ({ ...p, auth_value: e.target.value }))} placeholder="Token or key" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
              </div>
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Response Path</label>
                <input value={configForm.response_path} onChange={e => setConfigForm(p => ({ ...p, response_path: e.target.value }))} placeholder="e.g. data.investors (JSON path to array)" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md font-mono text-xs" /></div>
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Field Mapping (JSON)</label>
                <textarea value={configForm.field_mapping} onChange={e => setConfigForm(p => ({ ...p, field_mapping: e.target.value }))} rows={4} placeholder='{"full_name": "investor_name", "nationality": "country", "date_of_birth": "dob"}' className="w-full mt-1 px-3 py-2 text-xs border border-[#E4E4E7] rounded-md font-mono" />
                <p className="text-[10px] text-[#A1A1AA] mt-1">Map our fields to API response fields. Use @static:value for constants.</p>
              </div>
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Default Case Type</label>
                <select value={configForm.default_case_type} onChange={e => setConfigForm(p => ({ ...p, default_case_type: e.target.value }))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md"><option value="individual">Individual</option><option value="entity">Entity</option></select></div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowConfigForm(false)} className="px-4 py-2 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Save Config</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 6A: Case Creation Wizard Component ──────────────────────────
function CaseCreationWizard({ onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ case_type: 'individual', full_name: '', given_name: '', middle_name: '', family_name: '', gender: '', nationality: '', country_of_residence: '', date_of_birth: '', id_type: '', id_number: '', source_of_wealth: '', occupation: '', notes: '', registration_number: '', incorporation_country: '', business_type: '', industry_sector: '', account_id_ref: '', account_relation: '' });
  const [createdCase, setCreatedCase] = useState(null);
  const [screenResult, setScreenResult] = useState(null);
  const [riskResult, setRiskResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [relations, setRelations] = useState(RELATION_OPTIONS);

  useEffect(() => {
    api.get('/accounts', { params: { status: 'active', limit: 500 } })
      .then(r => { setAccounts(r.data.accounts || []); setRelations(r.data.default_relations || relations); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const STEPS = [
    { num: 1, label: 'Case Type' },
    { num: 2, label: 'Identity' },
    { num: 3, label: 'Auto-Screen' },
    { num: 4, label: 'Review Results' },
    { num: 5, label: 'Risk Rating' },
  ];

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = (label, key, opts = {}) => (
    <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">{label}</label>
      <input value={form[key]} onChange={e => set(key, e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" {...opts} /></div>
  );

  const handleCreateAndScreen = async () => {
    setLoading(true);
    try {
      // For individuals, assemble full_name from First/Middle/Last (backward compatible
      // — backend still receives a full_name string; screening engine unchanged).
      // For entities, keep whatever the user typed in full_name.
      const payload = { ...form, source: 'Manual' };
      if (form.case_type === 'individual') {
        const parts = [form.given_name, form.middle_name, form.family_name]
          .map(s => (s || '').trim())
          .filter(Boolean);
        payload.full_name = parts.join(' ');
      }
      // Step 3: Create case
      const caseRes = await api.post('/cases', payload);
      setCreatedCase(caseRes.data);
      // Step 3: Auto-screen
      const screenRes = await api.post(`/cases/${caseRes.data.id}/screen`);
      setScreenResult(screenRes.data);
      setStep(4);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to create/screen case'); }
    setLoading(false);
  };

  const handleCalculateRisk = async () => {
    if (!createdCase) return;
    setLoading(true);
    try {
      const r = await api.post(`/risk/calculate/${createdCase.id}`);
      setRiskResult(r.data);
      setStep(5);
    } catch (e) { alert('Risk calculation failed'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-md border border-[#E4E4E7] shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        {/* Wizard Header */}
        <div className="px-6 pt-5 pb-3 border-b border-[#E4E4E7]">
          <h2 className="font-heading text-lg font-semibold">New Case Wizard</h2>
          <div className="flex items-center gap-1 mt-3">
            {STEPS.map(s => (
              <div key={s.num} className="flex items-center gap-1 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.num ? 'bg-[#0052FF] text-white' : 'bg-[#F0F0F1] text-[#A1A1AA]'}`}>{s.num}</div>
                <span className={`text-[10px] ${step >= s.num ? 'text-[#0A0A0A] font-medium' : 'text-[#A1A1AA]'}`}>{s.label}</span>
                {s.num < 5 && <div className={`flex-1 h-px ${step > s.num ? 'bg-[#0052FF]' : 'bg-[#E4E4E7]'} mx-1`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Case Type */}
          {step === 1 && (
            <div>
              <p className="text-sm text-[#52525B] mb-4">Select the type of entity being onboarded.</p>
              <div className="grid grid-cols-2 gap-4">
                {['individual', 'entity'].map(t => (
                  <button key={t} onClick={() => { set('case_type', t); setStep(2); }} className={`p-6 border rounded-md text-center transition-all hover:border-[#0052FF] ${form.case_type === t ? 'border-[#0052FF] bg-blue-50/30' : 'border-[#E4E4E7]'}`}>
                    <p className="text-lg font-semibold capitalize">{t}</p>
                    <p className="text-xs text-[#A1A1AA] mt-1">{t === 'individual' ? 'Natural person (KYC)' : 'Company / Organisation'}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Identity */}
          {step === 2 && (
            <div>
              <p className="text-sm text-[#52525B] mb-4">Enter {form.case_type === 'individual' ? 'personal' : 'entity'} identification details.</p>
              <div className="space-y-3">
                {/* Account + Relation */}
                <div className="p-3 rounded-md bg-[#F8FAFC] border border-[#E4E4E7]">
                  <p className="text-[11px] uppercase tracking-wider text-[#0D7377] font-semibold mb-2">Account Linkage</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Account</label>
                      <select value={form.account_id_ref} onChange={e => set('account_id_ref', e.target.value)} data-testid="wizard-account-select" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md bg-white">
                        <option value="">— No account —</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.account_id})</option>)}
                      </select>
                      {accounts.length === 0 && <p className="text-[10px] text-[#A1A1AA] mt-0.5">No active accounts yet. Create one from the Accounts page.</p>}
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Relation</label>
                      <select value={form.account_relation} onChange={e => set('account_relation', e.target.value)} disabled={!form.account_id_ref} data-testid="wizard-relation-select" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md bg-white disabled:bg-[#F0F0F1]">
                        <option value="">— Select —</option>
                        {relations.map(r => <option key={r} value={r}>{displayRelation(r)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                {form.case_type === 'individual' ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">First Name *</label>
                        <input value={form.given_name} onChange={e => set('given_name', e.target.value)} data-testid="case-first-name" required autoFocus className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
                        {errors.given_name && <p className="text-xs text-red-600 mt-0.5">{errors.given_name}</p>}
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Middle Name <span className="text-[#A1A1AA] normal-case tracking-normal">(optional)</span></label>
                        <input value={form.middle_name} onChange={e => set('middle_name', e.target.value)} data-testid="case-middle-name" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Last Name *</label>
                        <input value={form.family_name} onChange={e => set('family_name', e.target.value)} data-testid="case-last-name" required className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
                        {errors.family_name && <p className="text-xs text-red-600 mt-0.5">{errors.family_name}</p>}
                      </div>
                    </div>
                    {errors.full_name && <p className="text-xs text-red-600 -mt-2">{errors.full_name}</p>}
                    <div className="grid grid-cols-2 gap-3">
                      <CountrySelect label="Nationality *" value={form.nationality} onChange={v => set('nationality', v)} error={errors.nationality} />
                      <CountrySelect label="Country of Residence" value={form.country_of_residence} onChange={v => set('country_of_residence', v)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        {inp('Date of Birth', 'date_of_birth', { type: 'date', max: new Date().toISOString().split('T')[0] })}
                        {errors.date_of_birth && <p className="text-xs text-red-600">{errors.date_of_birth}</p>}
                      </div>
                      {inp('Occupation', 'occupation')}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Gender <span className="text-[#A1A1AA] normal-case tracking-normal">(optional)</span></label>
                        <select
                          data-testid="case-gender-select"
                          value={form.gender}
                          onChange={e => set('gender', e.target.value)}
                          className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md"
                        >
                          <option value="">— Unspecified —</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                        <p className="text-[10px] text-[#A1A1AA] mt-1">Optional post-filter during screening. Blank keeps default matching.</p>
                      </div>
                      <div />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">ID Type</label>
                        <select value={form.id_type} onChange={e => set('id_type', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
                          <option value="">--</option>{ID_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select></div>
                      <div>
                        {inp('ID Number', 'id_number')}
                        {errors.id_number && <p className="text-xs text-red-600">{errors.id_number}</p>}
                      </div>
                    </div>
                    {inp('Source of Wealth', 'source_of_wealth')}
                  </>
                ) : (
                  <>
                    {inp('Legal Name *', 'full_name', { required: true, autoFocus: true })}
                    {errors.full_name && <p className="text-xs text-red-600 -mt-2">{errors.full_name}</p>}
                    <div className="grid grid-cols-2 gap-3">
                      {inp('Registration Number', 'registration_number')}
                      <CountrySelect label="Incorporation Country *" value={form.incorporation_country} onChange={v => set('incorporation_country', v)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Business Type</label>
                        <select value={form.business_type} onChange={e => set('business_type', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
                          <option value="">--</option><option>LLC</option><option>Corporation</option><option>Trust</option><option>Partnership</option><option>Foundation</option>
                        </select></div>
                      {inp('Industry Sector', 'industry_sector')}
                    </div>
                    <CountrySelect label="Nationality / Jurisdiction" value={form.nationality} onChange={v => set('nationality', v)} error={errors.nationality} />
                  </>
                )}
                <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Notes</label>
                  <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
              </div>
            </div>
          )}

          {/* Step 3: Auto-Screen */}
          {step === 3 && (
            <div className="text-center py-8" data-testid="wizard-step-3">
              {loading ? (
                <>
                  <div className="relative w-20 h-20 mx-auto mb-5">
                    <div className="absolute inset-0 w-20 h-20 border-4 border-[#E4E4E7] rounded-full" />
                    <div className="absolute inset-0 w-20 h-20 border-4 border-[#0052FF] border-t-transparent border-r-transparent rounded-full animate-spin" data-testid="wizard-loading-spinner" />
                    <MagnifyingGlass size={28} className="absolute inset-0 m-auto text-[#0052FF] animate-pulse" />
                  </div>
                  <p className="text-sm font-medium text-[#0A0A0A]">Creating case and running screening against 90,000+ entries…</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">Sanctions, PEP, and Watchlist databases</p>
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#52525B]">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0052FF] animate-pulse" />
                    <span>This may take a few seconds — please don't close this window</span>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-[#52525B] mb-4">Ready to create the case and auto-screen <strong>{form.full_name}</strong> against all databases.</p>
                  <button onClick={handleCreateAndScreen} data-testid="wizard-create-screen-btn" className="px-6 py-2.5 bg-[#0052FF] text-white rounded-md text-sm font-medium hover:bg-[#0040CC]">Create & Screen Now</button>
                </>
              )}
            </div>
          )}

          {/* Step 4: Review Results */}
          {step === 4 && screenResult && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded text-sm font-bold ${screenResult.status === 'match' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                  {screenResult.status === 'match' ? `${screenResult.total_matches} Match(es) Found` : 'Clear — No Matches'}
                </span>
              </div>
              {screenResult.matches?.slice(0, 5).map((m, i) => (
                <div key={i} className="border border-[#E4E4E7] rounded-md p-3 mb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium">{m.entry?.name || m.matched_name}</p>
                      <p className="text-xs text-[#52525B]">{m.database} • {m.entry?.source || ''}</p>
                      {m.entry?.aliases?.length > 0 && <p className="text-[10px] text-[#A1A1AA]">Aliases: {m.entry.aliases.slice(0, 3).join(', ')}</p>}
                    </div>
                    <span className={`text-sm font-bold ${m.match_score >= 80 ? 'text-red-600' : m.match_score >= 60 ? 'text-orange-600' : 'text-yellow-600'}`}>{m.match_score?.toFixed(1)}%</span>
                  </div>
                </div>
              ))}
              {screenResult.total_matches > 5 && <p className="text-xs text-[#A1A1AA] mt-2">+ {screenResult.total_matches - 5} more matches. View full details on the case page.</p>}
            </div>
          )}

          {/* Step 5: Risk Rating */}
          {step === 5 && riskResult && (
            <div>
              <div className="flex items-center gap-6 mb-4">
                <div>
                  <p className="text-xs uppercase text-[#52525B]">Auto-Calculated Rating</p>
                  <span className={`inline-block mt-1 px-4 py-1.5 rounded text-lg font-bold border ${riskResult.risk_rating === 'Low' ? 'bg-green-50 text-green-700 border-green-200' : riskResult.risk_rating === 'Medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : riskResult.risk_rating === 'High' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{riskResult.risk_rating}</span>
                </div>
                <div>
                  <p className="text-xs uppercase text-[#52525B]">Score</p>
                  <p className="text-3xl font-bold">{riskResult.risk_score}<span className="text-sm text-[#A1A1AA] font-normal">/100</span></p>
                </div>
              </div>
              <h4 className="text-xs uppercase tracking-wider font-medium text-[#52525B] mb-2">Risk Factors</h4>
              {riskResult.factors?.map((f, i) => (
                <div key={i} className="flex justify-between py-2 border-b border-[#F0F0F1] text-sm">
                  <div><p className="font-medium">{f.factor}</p><p className="text-xs text-[#A1A1AA]">{f.detail}</p></div>
                  <span className={`font-bold ${f.contribution >= 20 ? 'text-red-600' : 'text-green-600'}`}>+{f.contribution}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-between">
          <button onClick={step === 1 ? onClose : () => setStep(s => Math.max(1, s - 1))} className="px-4 py-2 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]" disabled={loading}>
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step === 2 && <button onClick={() => {
            const errs = {};
            if (form.case_type === 'individual') {
              // For individuals validate First + Last separately; Middle stays optional.
              const first = (form.given_name || '').trim();
              const last = (form.family_name || '').trim();
              if (!first) errs.given_name = 'First name is required';
              if (!last) errs.family_name = 'Last name is required';
              // Only run legacy full-name char validation when both are present
              if (first && last) {
                const combined = [first, (form.middle_name || '').trim(), last].filter(Boolean).join(' ');
                const nameErr = validateName(combined);
                if (nameErr) errs.given_name = nameErr;
              }
            } else {
              const nameErr = validateName(form.full_name);
              if (nameErr) errs.full_name = nameErr;
            }
            const dobErr = validateDOB(form.date_of_birth);
            if (dobErr) errs.date_of_birth = dobErr;
            const idErr = validateIdNumber(form.id_type, form.id_number);
            if (idErr) errs.id_number = idErr;
            if (form.case_type === 'individual' && !form.nationality) errs.nationality = 'Nationality is required';
            if (form.case_type === 'entity' && !form.incorporation_country && !form.nationality) errs.nationality = 'Jurisdiction is required';
            if (Object.keys(errs).length) { setErrors(errs); return; }
            setErrors({}); setStep(3);
          }} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Next: Auto-Screen</button>}
          {step === 4 && <button onClick={handleCalculateRisk} disabled={loading} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">{loading ? 'Calculating...' : 'Next: Assess Risk'}</button>}
          {step === 5 && <button onClick={() => onComplete(createdCase?.id)} className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700">Complete — View Case</button>}
        </div>
      </div>
    </div>
  );
}


// 2A: Searchable Country Dropdown Component
function CountrySelect({ label, value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = filterCountries(query || value);
  return (
    <div className="relative">
      <label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">{label}</label>
      <input value={value || query} onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} placeholder="Type to search..." className={`w-full mt-1 px-3 py-2 text-sm border rounded-md ${error ? 'border-red-400' : 'border-[#E4E4E7]'}`} />
      {error && <p className="text-xs text-red-600 mt-0.5">{error}</p>}
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#E4E4E7] rounded-md shadow-lg max-h-40 overflow-auto">
          {filtered.map(c => (
            <div key={c} onClick={() => { onChange(c); setQuery(''); setOpen(false); }} className="px-3 py-1.5 text-sm cursor-pointer hover:bg-[#F0F0F1]">{c}</div>
          ))}
        </div>
      )}
    </div>
  );
}
